import { useState, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material';
import {
  Image as ImageIcon,
  Movie as MovieIcon,
  Audiotrack as AudioIcon,
  Search as SearchIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { WorkspaceField, defaultCreateWorkspace } from '../WorkspaceField';
import {
  creativesService,
  Creative,
  CreativeType,
} from '../../services/creatives.service';

interface CreativeLibraryPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (creative: Creative) => void;
  /**
   * Restrict the picker to a single asset type. Defaults to all types.
   */
  type?: CreativeType;
  title?: string;
  /**
   * Allow the user to upload a new asset directly from the picker
   * (defaults to true). The uploaded asset is added to the library and
   * immediately selected.
   */
  allowUpload?: boolean;
}

type Tab = 'library' | 'upload';

export default function CreativeLibraryPicker({
  open,
  onClose,
  onSelect,
  type,
  title = 'Select a creative',
  allowUpload = true,
}: CreativeLibraryPickerProps) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('library');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<CreativeType | 'all'>(type || 'all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  // No ad set ties a library upload to a workspace yet, so it asks.
  const [workspaceId, setWorkspaceId] = useState(defaultCreateWorkspace);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const effectiveType = type || (typeFilter === 'all' ? undefined : typeFilter);

  const { data, isLoading } = useQuery({
    queryKey: ['creatives-library', effectiveType, search],
    queryFn: () =>
      creativesService.list({
        type: effectiveType,
        search: search.trim() || undefined,
        limit: 100,
      }),
    enabled: open && tab === 'library',
  });

  const creatives = useMemo(() => data?.data || [], [data]);
  const selectedCreative = creatives.find((c) => c.id === selectedId) || null;

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      setUploadProgress(10);
      const result = await creativesService.upload(file, {
        workspaceId,
        name: uploadName || file.name,
        source: 'upload',
      });
      setUploadProgress(100);
      return result;
    },
    onSuccess: (creative) => {
      setUploadError(null);
      queryClient.invalidateQueries({ queryKey: ['creatives-library'] });
      queryClient.invalidateQueries({ queryKey: ['creatives'] });
      // Auto-select & confirm the new asset
      onSelect(creative);
      handleClose();
    },
    onError: (err: Error) => {
      setUploadError(err.message || 'Upload failed');
      setUploadProgress(0);
    },
  });

  const handleClose = () => {
    setTab('library');
    setSearch('');
    setSelectedId(null);
    setUploadFile(null);
    setWorkspaceId(defaultCreateWorkspace());
    setUploadName('');
    setUploadProgress(0);
    setUploadError(null);
    onClose();
  };

  const handleConfirm = () => {
    if (!selectedCreative) return;
    onSelect(selectedCreative);
    handleClose();
  };

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      setUploadName(file.name);
      setUploadError(null);
    }
  };

  const handleUpload = () => {
    if (uploadFile) {
      uploadMutation.mutate(uploadFile);
    }
  };

  const renderThumb = (creative: Creative) => {
    if (creative.type === 'image' && (creative.thumbnail_url || creative.signed_url)) {
      return (
        <img
          src={creative.thumbnail_url || creative.signed_url}
          alt={creative.name}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      );
    }
    if (creative.type === 'video' && creative.signed_url) {
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.900',
          }}
        >
          <MovieIcon sx={{ fontSize: 48, color: 'grey.500' }} />
        </Box>
      );
    }
    if (creative.type === 'audio') {
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.900',
          }}
        >
          <AudioIcon sx={{ fontSize: 48, color: 'grey.500' }} />
        </Box>
      );
    }
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.200',
        }}
      >
        <ImageIcon sx={{ fontSize: 48, color: 'grey.500' }} />
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {title}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {allowUpload && (
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}
        >
          <Tab label="Library" value="library" />
          <Tab label="Upload new" value="upload" icon={<UploadIcon />} iconPosition="start" />
        </Tabs>
      )}

      <DialogContent sx={{ minHeight: 480 }}>
        {tab === 'library' && (
          <>
            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  },
                }}
                sx={{ flex: 1 }}
              />
              {!type && (
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={typeFilter}
                  onChange={(_, v) => v && setTypeFilter(v)}
                >
                  <ToggleButton value="all">All</ToggleButton>
                  <ToggleButton value="image">
                    <ImageIcon fontSize="small" sx={{ mr: 0.5 }} /> Image
                  </ToggleButton>
                  <ToggleButton value="video">
                    <MovieIcon fontSize="small" sx={{ mr: 0.5 }} /> Video
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>

            {/* Grid */}
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : creatives.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No creatives in your library yet.
                  {allowUpload && ' Use the "Upload new" tab to add one.'}
                </Typography>
              </Box>
            ) : (
              <ImageList cols={4} gap={8} sx={{ m: 0 }}>
                {creatives.map((creative) => {
                  const isSelected = selectedId === creative.id;
                  return (
                    <ImageListItem
                      key={creative.id}
                      onClick={() => setSelectedId(creative.id)}
                      sx={{
                        cursor: 'pointer',
                        border: 2,
                        borderColor: isSelected ? 'primary.main' : 'transparent',
                        borderRadius: 1,
                        overflow: 'hidden',
                        position: 'relative',
                        height: 180,
                        '&:hover': { borderColor: isSelected ? 'primary.main' : 'grey.400' },
                      }}
                    >
                      {renderThumb(creative)}
                      {isSelected && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CheckIcon fontSize="small" />
                        </Box>
                      )}
                      <ImageListItemBar
                        title={creative.name}
                        subtitle={
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                              label={creative.type}
                              size="small"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                            {creative.tags?.slice(0, 2).map((t) => (
                              <Chip
                                key={t}
                                label={t}
                                size="small"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            ))}
                          </Box>
                        }
                      />
                    </ImageListItem>
                  );
                })}
              </ImageList>
            )}
          </>
        )}

        {tab === 'upload' && (
          <Box sx={{ py: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              hidden
              onChange={handleFilePick}
            />
            <Box
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: 2,
                borderStyle: 'dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 6,
                textAlign: 'center',
                cursor: 'pointer',
                mb: 2,
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}
            >
              <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1">
                {uploadFile ? uploadFile.name : 'Click to choose a file'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Image or video, up to 100 MB
              </Typography>
            </Box>

            {uploadFile && (
              <TextField
                fullWidth
                label="Name"
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                sx={{ mb: 2 }}
              />
            )}
            {uploadFile && (
              <Box sx={{ mb: 2 }}><WorkspaceField value={workspaceId} onChange={setWorkspaceId} /></Box>
            )}

            {uploadProgress > 0 && uploadProgress < 100 && (
              <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 2 }} />
            )}

            {uploadError && <Alert severity="error" sx={{ mb: 2 }}>{uploadError}</Alert>}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {tab === 'library' && (
          <Button
            variant="contained"
            disabled={!selectedCreative}
            onClick={handleConfirm}
          >
            Use this creative
          </Button>
        )}
        {tab === 'upload' && (
          <Button
            variant="contained"
            disabled={!uploadFile || !workspaceId || uploadMutation.isPending}
            onClick={handleUpload}
            startIcon={uploadMutation.isPending ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {uploadMutation.isPending ? 'Uploading...' : 'Upload & select'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
